const THETA = 0.8;

export default class BarnesHutTree {
  constructor() {
    this.root = null;
  }

  build(positions, count) {
    if (count === 0) { this.root = null; return; }

    // Find bounds
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = 0; i < count; i++) {
      const x = positions[i * 2];
      const y = positions[i * 2 + 1];
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }

    // Make square with padding
    const pad = 1;
    const size = Math.max(maxX - minX, maxY - minY) + pad * 2;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    this.root = { cx: 0, cy: 0, mass: 0, size, x: cx - size / 2, y: cy - size / 2, children: null, bodyIndex: -1 };

    for (let i = 0; i < count; i++) {
      this._insert(this.root, i, positions[i * 2], positions[i * 2 + 1]);
    }
  }

  _insert(node, index, px, py) {
    if (node.mass === 0) {
      // Empty node — place body here
      node.bodyIndex = index;
      node.cx = px;
      node.cy = py;
      node.mass = 1;
      return;
    }

    if (node.children === null) {
      // Leaf with one body — subdivide
      node.children = new Array(4);
      const halfSize = node.size / 2;
      for (let q = 0; q < 4; q++) {
        const qx = node.x + (q & 1 ? halfSize : 0);
        const qy = node.y + (q & 2 ? halfSize : 0);
        node.children[q] = { cx: 0, cy: 0, mass: 0, size: halfSize, x: qx, y: qy, children: null, bodyIndex: -1 };
      }
      // Re-insert existing body
      const oldIdx = node.bodyIndex;
      node.bodyIndex = -1;
      const oldQ = this._quadrant(node, node.cx, node.cy);
      this._insert(node.children[oldQ], oldIdx, node.cx, node.cy);
    }

    // Update center of mass
    const totalMass = node.mass + 1;
    node.cx = (node.cx * node.mass + px) / totalMass;
    node.cy = (node.cy * node.mass + py) / totalMass;
    node.mass = totalMass;

    // Insert into appropriate child
    const q = this._quadrant(node, px, py);
    this._insert(node.children[q], index, px, py);
  }

  _quadrant(node, px, py) {
    const halfSize = node.size / 2;
    const midX = node.x + halfSize;
    const midY = node.y + halfSize;
    let q = 0;
    if (px >= midX) q |= 1;
    if (py >= midY) q |= 2;
    return q;
  }

  computeForce(index, px, py, forces, gravConst) {
    if (!this.root || this.root.mass === 0) return;
    this._walkForce(this.root, index, px, py, forces, gravConst);
  }

  _walkForce(node, index, px, py, forces, gravConst) {
    if (node.mass === 0) return;

    if (node.bodyIndex === index && node.children === null) return;

    const dx = node.cx - px;
    const dy = node.cy - py;
    const distSq = dx * dx + dy * dy + 0.01; // softening
    const dist = Math.sqrt(distSq);

    if (node.children === null || (node.size / dist) < THETA) {
      // Treat as single body (or is single body)
      if (node.bodyIndex === index) return;
      // ForceAtlas2-style: 1/r repulsion (not 1/r² Newtonian)
      const force = gravConst * node.mass / dist;
      forces[0] += force * dx / dist;
      forces[1] += force * dy / dist;
      return;
    }

    // Recurse into children
    for (let q = 0; q < 4; q++) {
      if (node.children[q]) {
        this._walkForce(node.children[q], index, px, py, forces, gravConst);
      }
    }
  }
}
